import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {UpdateUserModel, UserModel, UserNotificationSettings, UserRole} from '../../../../types/control';
import {onClickHandler} from '../../../../utils/onclick';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {useKeyboard} from '../../../../hooks/keyboard';
import {hasAuthority} from '../../../../utils/role';
import {SelectItem} from '../../../../components/Select';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useQuestion} from '../../../../hooks/question';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/settings/users';

type Props = {
    onClick: (approved: boolean) => void;
    name: string;
    allowed: boolean;
};

const NotificationSetting = memo(function NotificationSetting({name, allowed, onClick}: Props) {
    return (
        <div className="line">
            <span>{name}</span>
            <div className="half">
                <div className="half" {...onClickHandler(() => onClick(true))}>
                    <div className={'check-box' + (allowed ? ' checked' : '')}/>
                </div>
                <div className="half" {...onClickHandler(() => onClick(false))}>
                    <div className={'check-box' + (allowed ? '' : ' checked')}/>
                </div>
            </div>
        </div>
    );
});

export const UserEditScreen = memo(function UserEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string; }>();
    const history = useHistory();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [rpc] = useRPC();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);

    const userList = useSelector(state => state.control.userList);
    const currentUsersRole = useSelector(state => state.ui.role);

    const ROLE_SELECTION_ITEMS = useMemo<SelectItem<UserRole>[]>(() => [
        {value: 'user', label: t('user')},
        {value: 'admin', label: t('admin')},
        {value: 'service', label: t('service')},
        {value: 'developer', label: t('developer')},
    ], [t]);
    const options = useMemo<SelectItem<UserRole>[]>(
        () => ROLE_SELECTION_ITEMS.filter(m => hasAuthority(currentUsersRole, m.value)),
        [ROLE_SELECTION_ITEMS, currentUsersRole],
    );

    const [selectedUser, setSelectedUser] = useState<UserModel>(null);
    const [name, setName] = useState('');
    const [role, setRole] = useState<SelectItem<UserRole>>(options[0]);
    const [accountPassword, setAccountPassword] = useState('');
    const [alarmPassword, setAlarmPassword] = useState('');
    const [notificationSettings, setNotificationSettings] = useState<UserNotificationSettings>({
        alarm: true,
        intercom: true,
        bell: true,
        intertext: true,
    });

    const toggleNotificationSelected = useCallback((name: string, allowed: boolean) => {
        setNotificationSettings(notificationSettings => ({...notificationSettings, [name]: allowed}));
    }, []);

    const getValidationErrorMessage = useCallback(() => {
        if (!name || name.length < 3 || name === 'default') {
            return t('pls_enter_a_valid_username');
        }

        if ((!selectedUser && !accountPassword) || (accountPassword && accountPassword.length !== 6)) {
            return t('login_password_must_be_6_digits');
        }

        if ((!selectedUser && !alarmPassword) || (alarmPassword && alarmPassword.length !== 4)) {
            return t('alar_password_must_be_4_digits');
        }

        return null;
    }, [t, accountPassword, alarmPassword, name, selectedUser]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const deletePressed = useCallback(() => {
        question(t('are_you_sure_you_want_to_delete'), () => {
            return rpc.deleteUser(selectedUser.id)
                .then(() => {
                    success(t('user_successfully_deleted'));
                    onBackPressed();
                }).catch(failure);
        });
    }, [t, failure, onBackPressed, question, rpc, selectedUser, success]);

    const createPressed = useCallback(() => {
        const message = getValidationErrorMessage();
        if (message) {
            failure(message);

            return;
        }

        const user: UpdateUserModel = {
            ...(selectedUser ? selectedUser : {id: null}),
            role: role.value,
            username: name,
            notification_settings: notificationSettings,
            password: accountPassword,
            alarm_password: alarmPassword,
        };

        return rpc.updateUser(user)
            .then(() => {
                success(t(user.id != null ? 'user_successfully_edited' : 'user_successfully_created'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        getValidationErrorMessage,
        selectedUser,
        role,
        name,
        notificationSettings,
        accountPassword,
        alarmPassword,
        rpc,
        failure,
        success,
        onBackPressed,
    ]);

    const onFocusPassword = useCallback(() => {
        if (accountPassword === '******') {
            setAccountPassword('');
        }
    }, [accountPassword]);

    const onFocusAlarmPassword = useCallback(() => {
        if (alarmPassword === '****') {
            setAlarmPassword('');
        }
    }, [alarmPassword]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() => [
        {
            text: t('delete'),
            className: 'mulberry-wood',
            onClick: deletePressed,
            hidden: !selectedUser,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: createPressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            onClick: onBackPressed,
        },
    ], [t, deletePressed, selectedUser, createPressed, onBackPressed]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const id = params.id;
        const user = userList.find(user => user.id === id);
        if (user == null) {
            return;
        }

        setSelectedUser(user);
        setName(user.username);
        setAccountPassword('******');
        setAlarmPassword('****');
        setRole(options.find(option => option.value === user.role));
        setNotificationSettings(user.notification_settings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='user' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_username')}
                            label={t('username')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={6}
                            placeholder={t('write_login_password')}
                            label={t('login_password')}
                            value={accountPassword}
                            type="number-password"
                            setValue={setAccountPassword}
                            setActiveInput={setActiveNumpadInput}
                            onFocus={onFocusPassword}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={4}
                            placeholder={t('write_alarm_password')}
                            label={t('alarm_password')}
                            type="number-password"
                            value={alarmPassword}
                            setValue={setAlarmPassword}
                            setActiveInput={setActiveNumpadInput}
                            onFocus={onFocusAlarmPassword}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('authorization_level')}
                            options={options}
                            onChange={setRole}
                            value={role}/>
                        {
                            renderIf(selectedUser)(() =>
                                <>
                                    <SettingReadOnlyRowComponent
                                        title='Mobil OS'
                                        value={selectedUser.mobile_os}/>
                                    <SettingReadOnlyRowComponent
                                        title='Push Token'
                                        value={selectedUser.push_token}/>
                                </>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">
                        <div className="table-title">
                            <div className="half">
                                <div className="flex">
                                    <span>{t('notification_settings')}</span>
                                </div>
                            </div>
                            <div className="half">
                                <div className="half">
                                    <span>{t('allow')}</span>
                                </div>
                                <div className="half">
                                    <span>{t('do_not_allow')}</span>
                                </div>
                            </div>
                        </div>
                        <div className="notification-list">
                            <NotificationSetting
                                name={t('alarm')}
                                allowed={notificationSettings.alarm}
                                onClick={isApproved => toggleNotificationSelected('alarm', isApproved)}/>
                            <NotificationSetting
                                name={t('intercom')}
                                allowed={notificationSettings.intercom}
                                onClick={isApproved => toggleNotificationSelected('intercom', isApproved)}/>
                            <NotificationSetting
                                name={t('bell')}
                                allowed={notificationSettings.bell}
                                onClick={isApproved => toggleNotificationSelected('bell', isApproved)}/>
                            <NotificationSetting
                                name={t('inter_text')}
                                allowed={notificationSettings.intertext}
                                onClick={isApproved => toggleNotificationSelected('intertext', isApproved)}/>
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
